<script>
import { MESSAGE_TYPE } from 'widget/helpers/constants';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { ATTACHMENT_ICONS } from 'shared/constants/messages';
import { CONTENT_TYPES } from 'shared/constants/contentType';
import { getCsatSurveyResponse } from './CardCsat/utils';
import { parseStringToObject } from './CardMessage/utils';
export default {
  name: 'MessagePreview',
  props: {
    message: {
      type: Object,
      required: true,
    },
    showMessageType: {
      type: Boolean,
      default: true,
    },
    defaultEmptyMessage: {
      type: String,
      default: '',
    },
    showSenderName: {
      type: Boolean,
      default: false,
    },
    /** 以[picture]和[video]的形式显示图片和视频 */
    showPictureAndVideoContent: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { getPlainText } = useMessageFormatter();
    return {
      getPlainText,
    };
  },
  computed: {
    messageByAgent() {
      const { message_type: messageType } = this.message;
      return messageType === MESSAGE_TYPE.OUTGOING;
    },
    isMessageAnActivity() {
      const { message_type: messageType } = this.message;
      return messageType === MESSAGE_TYPE.ACTIVITY;
    },
    isMessagePrivate() {
      const { private: isPrivate } = this.message;
      return isPrivate;
    },
    allAttachmentsIsPicOrVideo() {
      return this.message.attachments?.every((attachment) => {
        return attachment.file_type === 'image' || attachment.file_type === 'video';
      });
    },
    pictureAndVideoContent() {
      return this.message.attachments?.map((attachment) => {
        return attachment.file_type === 'image' ? '[Photo]' : (attachment.file_type === 'video' ? '[Video]' : '');
      }).join(' ');
    },
    parsedLastMessage() {
      const { content_attributes: contentAttributes } = this.message;
      const { email: { subject } = {} } = contentAttributes || {};
      if (this.showPictureAndVideoContent && this.message.attachments?.length > 0) {
        return this.getPlainText(subject || this.message.content) + this.pictureAndVideoContent;
      }
      return this.getPlainText(subject || this.message.content);
    },
    lastMessageFileType() {
      const [{ file_type: fileType } = {}] = this.message.attachments;
      return fileType;
    },
    attachmentIcon() {
      return ATTACHMENT_ICONS[this.lastMessageFileType];
    },
    attachmentMessageContent() {
      if (this.showPictureAndVideoContent && this.allAttachmentsIsPicOrVideo) {
        return this.pictureAndVideoContent;
      }
      return `CHAT_LIST.ATTACHMENTS.${this.lastMessageFileType}.CONTENT`;
    },
    isMessageSticker() {
      return this.message && this.message.content_type === 'sticker';
    },
    // 消息卡片
    isCardMessage() {
      return this.message && this.message.content_type === CONTENT_TYPES.CARD;
    },
    // 评价卡片
    isCsatMessage() {
      return this.message && this.message.content_type === CONTENT_TYPES.CSAT;
    },
    // 是否已回复评价
    isCsatMessageReplied() {
      return getCsatSurveyResponse(this.message);
    },
    // 发送者名字
    senderName() {
      return this.message.sender?.name || '';
    },
    cardMessageContent() {
      const parsedContent = parseStringToObject(this.message.content) || {};
      const cardType = parsedContent.cardType;
      if (cardType === 'order') {
        return `[Order Details] ${parsedContent.data?.showStatus}`;
      }
      if (cardType === 'goods') {
        return `[Product Details] ${parsedContent.data?.goodsName}`;
      }
      if (cardType === 'reverse_order') {
        return `[Return Details] ${parsedContent.data?.showStatus}`;
      }
      return '';
    },
  },
};
</script>

<template>
  <div class="overflow-hidden text-ellipsis whitespace-nowrap message-preview-text">
    <template v-if="showMessageType">
      <fluent-icon
        v-if="isMessagePrivate"
        size="16"
        class="-mt-0.5 align-middle text-slate-600 dark:text-slate-300 inline-block"
        icon="lock-closed"
      />
      <fluent-icon
        v-else-if="messageByAgent"
        size="16"
        class="-mt-0.5 align-middle text-slate-600 dark:text-slate-300 inline-block"
        icon="arrow-reply"
      />
      <fluent-icon
        v-else-if="isMessageAnActivity"
        size="16"
        class="-mt-0.5 align-middle text-slate-600 dark:text-slate-300 inline-block"
        icon="info"
      />
    </template>
    <span v-if="showSenderName && senderName">
      {{ `${senderName}: ` }}
    </span>
    <span v-if="message.content && isCsatMessage">
      <!-- 有评价时 -->
      <span v-if="isCsatMessageReplied">
        {{ $t('CHAT_LIST.ATTACHMENTS.csat.CONTENT') }}
      </span>
      <!-- 没有评价时 -->
      <span v-else>
        {{ $t('CHAT_LIST.ATTACHMENTS.csat.CONTENT-WAITING') }}
      </span>
    </span>
    <span v-else-if="message.content && isCardMessage">
      {{ cardMessageContent }}
    </span>
    <span v-else-if="message.content && isMessageSticker">
      <fluent-icon
        size="16"
        class="-mt-0.5 align-middle inline-block text-slate-600 dark:text-slate-300"
        icon="image"
      />
      {{ $t('CHAT_LIST.ATTACHMENTS.image.CONTENT') }}
    </span>
    <span v-else-if="message.content">
      {{ parsedLastMessage }}
    </span>
    <span v-else-if="message.attachments">
      <fluent-icon
        v-if="attachmentIcon && showMessageType"
        size="16"
        class="-mt-0.5 align-middle inline-block text-slate-600 dark:text-slate-300"
        :icon="attachmentIcon"
      />
      {{ $t(`${attachmentMessageContent}`) }}
    </span>
    <span v-else>
      {{ defaultEmptyMessage || $t('CHAT_LIST.NO_CONTENT') }}
    </span>
  </div>
</template>
